import { computed, effect, inject, untracked } from '@angular/core';
import { Search } from '@df/search/search.interfaces';
import { patchState, signalStoreFeature, type, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { setAllEntities, withEntities } from '@ngrx/signals/entities';
import { AdvancedCommerce } from '../../advanced-commerce.namespace';
import { AdvancedCommerceStore } from '../advanced-commerce-store.namespace';
import { AdvancedCommerceStoreService, injectRouteParams } from '../advanced-commerce-store.service';

const initialState: AdvancedCommerceStore.Search.State = {
    searchRequest: undefined,
    searchNoResults: {
        label: 'No results found. Try another search term.',
        items: []
    },
    searchPopularSearches: {
        label: 'Popular searches',
        items: []
    },
    searchCategories: {
        label: 'Suggested categories',
        items: []
    },
    searchHelp: {
        label: 'Can we help?',
        items: []
    },
    searchHeadings: {}
};

export function withSearch() {
    return signalStoreFeature(
        {
            state: type<AdvancedCommerceStore.State & AdvancedCommerceStore.Response.State & AdvancedCommerceStore.Meta.State>(),
            methods: type<{
                dataSet(data: AdvancedCommerceStore.Data): void;
                elementSet(element: AdvancedCommerceStore.Element): void;
            }>()
        },
        withState<AdvancedCommerceStore.Search.State>(initialState),
        withEntities({ entity: type<AdvancedCommerceStore.Items.Item>(), collection: 'noResultsItems' }),
        withEntities({ entity: type<AdvancedCommerceStore.Items.Item>(), collection: 'homeRecsItems' }),
        withComputed(store => {
            const routeParams = injectRouteParams();

            return {
                queryParamQ: computed<string>(() => routeParams()['q']),
                hasTerm: computed(() => !!store.searchRequest()?.q),
                hasSearchNoResults: computed(() => store.noResultsItemsEntities().length > 0),
                hasSearchPopularSearches: computed(() => store.searchPopularSearches().items.length > 0),
                hasSearchHelp: computed(() => store.searchHelp().items.length > 0)
            };
        }),
        withMethods(store => {
            return {
                searchRequestSet(searchRequest: Search.IRequest | undefined) {
                    patchState(store, { searchRequest });
                },
                setSearchNoResultsItems(items: AdvancedCommerceStore.Items.Item[]) {
                    patchState(store, setAllEntities(items, { collection: 'noResultsItems' }));
                },
                setSearchNoResults(searchNoResults: AdvancedCommerceStore.Search.NoResults) {
                    patchState(store, { searchNoResults });
                },
                setHomeRecsItems(items: AdvancedCommerceStore.Items.Item[]) {
                    if (items.length > 0) {
                        patchState(store, setAllEntities(items, { collection: 'homeRecsItems' }));
                    }
                },
                setSearchPopularSearches(searchPopularSearches: AdvancedCommerceStore.Search.PopularSearches) {
                    patchState(store, { searchPopularSearches });
                },
                setSearchCategories(searchCategories: AdvancedCommerceStore.Search.Categories) {
                    patchState(store, { searchCategories });
                },
                setSearchHelp(searchHelp: AdvancedCommerceStore.Search.Help) {
                    patchState(store, { searchHelp });
                },
                setSearchHeadings(searchHeadings: AdvancedCommerceStore.Search.AutocompleteHeadings) {
                    patchState(store, { searchHeadings });
                }
            };
        }),
        withHooks(store => {
            const advancedCommerceStoreService = inject(AdvancedCommerceStoreService);

            return {
                onInit() {
                    /**
                     * Watch for changes in the response and element and
                     * update search data
                     */
                    effect(() => {
                        const response = store.response();
                        const element = store.element();

                        if (response && element) {
                            const items = advancedCommerceStoreService.getItems('products', response, 'autocomplete_no_results');
                            const noResult = advancedCommerceStoreService.getNoResults(response);
                            const homeRecItems = advancedCommerceStoreService.getItems('products', response, 'autocomplete_home_recs');
                            const popularSearch = advancedCommerceStoreService.getPopularSearches(response, element);
                            const categories = advancedCommerceStoreService.getSearchesCategories(response, element);
                            const help = advancedCommerceStoreService.getHelp(response);
                            const headings = advancedCommerceStoreService.getAutocompleteHeadings(response);

                            untracked(() => {
                                store.setSearchNoResultsItems(items);
                                store.setSearchNoResults(noResult);
                                store.setHomeRecsItems(homeRecItems);
                                store.setSearchPopularSearches(popularSearch);
                                store.setSearchCategories(categories);
                                store.setSearchHelp(help);
                                store.setSearchHeadings(headings);
                            });
                        }
                    });

                    /**
                     * Watch for changes in the search request and set data
                     * to load new query request
                     */
                    effect(() => {
                        const request = store.searchRequest();

                        if (request) {
                            const data: AdvancedCommerceStore.Data = {
                                context: {
                                    search_text: request.q
                                }
                            };

                            if (request.autocomplete) {
                                data.include = ['settings', 'labels'];
                                data.template = 'autocomplete';

                                if (request.q) {
                                    data.search_text = request.q;
                                }
                            } else {
                                data.elements = [AdvancedCommerce.Query.Element.search];
                            }

                            if (store.metaIsPagingRequest()) {
                                data.is_paging_request = true;
                            }

                            untracked(() => {
                                if (request.autocomplete) {
                                    store.elementSet(AdvancedCommerce.Query.Element.autocomplete);
                                } else {
                                    if (request.q) {
                                        const pageUrl = advancedCommerceStoreService.getSearchPageUrl(request);

                                        if (!data.context) {
                                            data.context = {};
                                        }

                                        data.context.page_url = pageUrl;
                                    }

                                    store.elementSet(AdvancedCommerce.Query.Element.search);
                                }

                                store.dataSet(data);
                            });
                        }
                    });
                }
            };
        })
    );
}
